/**
 * Query param carrying the company sign-in hint, both on the app URL a sign-in link lands on and on the authorize
 * request it is forwarded to. Must match the name Keycloak's `ThriveEmailLookupAuthenticator` reads.
 */
const COMPANY_HINT_PARAM = "company";
/** Shape of a `Company.slug`. Mirrors the server's check so a junk value never reaches the authorize request. */
const COMPANY_SLUG = /^[a-z0-9][a-z0-9-]{0,79}$/;
/**
 * The company slug carried on [search], or null when there isn't a usable one.
 *
 * A sign-in link — a QR code on a poster, a link in a campaign email — lands the member on an app URL carrying
 * `?company=<slug>`. Keycloak keeps unrecognised authorize params as auth-session client notes, which is how the slug
 * reaches the sign-in page and pre-renders that company's SSO buttons. Forwarding it is the whole point: without it
 * the hint stops at the app and the member gets the generic sign-in page.
 *
 * Malformed values are dropped rather than passed through. The slug comes off a URL a stranger can edit, and these
 * links are printed, so a stale or mangled one should degrade to the ordinary sign-in page.
 */
export function companyHintFrom(search) {
    return companySlugFrom(new URLSearchParams(search).get(COMPANY_HINT_PARAM));
}
/** [raw] trimmed and lowercased when it is a well-formed `Company.slug`, else null. */
export function companySlugFrom(raw) {
    const slug = raw?.trim().toLowerCase();
    if (!slug || !COMPANY_SLUG.test(slug))
        return null;
    return slug;
}
/**
 * [companyHintFrom] shaped for `signinRedirect({ extraQueryParams })`, or undefined so the param is omitted entirely
 * rather than sent empty.
 *
 * Defaults to the current location, which is where a sign-in link leaves the value.
 */
export function companyHintParams(search = typeof window === "undefined" ? undefined : window.location.search) {
    if (search === undefined)
        return undefined;
    const slug = companyHintFrom(search);
    return slug ? { [COMPANY_HINT_PARAM]: slug } : undefined;
}
//# sourceMappingURL=companyHint.js.map